/**
 * Reduce a raw accelerometer stream to one sample per GPS point, and run the
 * IMU vibration gate over the result.
 *
 * This lives in `processors/` rather than beside the gate's constants in
 * `analysis/config/` because it depends on the sensor-fusion helper: the
 * whole point of the metric is that the stored stream is reduced EXACTLY as
 * the fusion reduces it. `analysis/config/` stays free of processor imports;
 * it owns the thresholds and the pure `classifyReducedSamples` verdict, and
 * this module supplies the reduction that feeds it.
 */

import type { AccelerometerReading } from '../domain/telemetry/raw/AccelerometerReading'
import type { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import {
  averageReadingsInWindow,
  firstWindowStartExclusiveMs,
} from './imuWindowAverage'
import {
  classifyReducedSamples,
  type GpsRateAccelSample,
  type ImuVibrationResult,
} from '../analysis/config/imuVibrationGate'

export type { GpsRateAccelSample }

/**
 * Reduce the accelerometer stream to one sample per GPS point, exactly the
 * way the sensor fusion does: average every reading in the window
 * (previous GPS timestamp, this GPS timestamp], with the first point's
 * window clamped to one median GPS interval. Identity for an equal-rate
 * stream; a low-pass for a full-rate one. Units are normalized to m/s².
 */
export function reduceAccelerometerToGpsRate(
  accelerometer: AccelerometerReading[],
  gpsPoints: GpsPoint[]
): GpsRateAccelSample[] {
  const samples: GpsRateAccelSample[] = []
  const firstWindowStartMs = firstWindowStartExclusiveMs(gpsPoints)
  let index = 0

  for (let i = 0; i < gpsPoints.length; i++) {
    const timestampMs = gpsPoints[i].timestamp.getTime()
    const window = averageReadingsInWindow(
      accelerometer,
      i > 0 ? gpsPoints[i - 1].timestamp.getTime() : firstWindowStartMs,
      timestampMs,
      index
    )
    index = window.index
    const reading = window.reading
    if (!reading) continue

    const scale = reading.unit === 'g' ? 9.81 : 1
    samples.push({
      x: reading.x * scale,
      y: reading.y * scale,
      z: reading.z * scale,
      timestampMs,
      speedKmh: gpsPoints[i].speedKmh,
    })
  }

  return samples
}

/**
 * Decide whether an accelerometer stream can be trusted: reduce it to GPS
 * rate the way the fusion reduces it, then apply the gate's thresholds.
 * See `analysis/config/imuVibrationGate.ts` for the rules and calibration.
 */
export function classifyImuVibration(
  accelerometer: AccelerometerReading[],
  gpsPoints: GpsPoint[]
): ImuVibrationResult {
  return classifyReducedSamples(
    reduceAccelerometerToGpsRate(accelerometer, gpsPoints)
  )
}
